import {
  CABLE_TYPE_LABELS,
  type CableTest,
  type CableType,
  describeCableTest,
  type DropColor,
  fiberModeOf,
  isOverLength,
  type LengthUnit,
} from './cable.ts';
import {
  converterConnectorAt,
  converterSideOfPort,
  connectorsMate,
  declaresConverterSides,
  hasWifi,
  isMediaConverter,
  isPanel as isPanelType,
  isPortList,
  panelCassetteCapacity,
  type PortConnector,
  portPositions,
} from './item-types.ts';
import { portGroupRole } from './port-groups.ts';
import {
  AV_MEDIA,
  type AvMedium,
  CONNECTION_MEDIUM_LABELS,
  COPPER_TERMINATIONS,
  DEFAULT_LINK_STATUS,
  FIBER_MODE_LABELS,
  FIBER_TERMINATIONS,
  LINK_STATUS_LABELS,
  type LinkStatus,
  LINK_TERMINATION_LABELS,
  type Connection,
  type ConnectionInput,
  type ConnectionMedium,
} from './schemas/connection.ts';
import { itemsIn, walkProperties } from './tree.ts';
import type { CableDrop, DropOrigin } from './schemas/cable-drop.ts';
import type { Item } from './schemas/item.ts';
import type { PortGroup } from './schemas/port-group.ts';
import type { ClientTree } from './schemas/responses.ts';
import type { PortStateName } from './wiring-colors.ts';

// The rules of a link, shared by the form (which offers only valid choices)
// and the Worker (which refuses everything else):
//   cable / fiber – both devices at the same property and within reach: the
//            same rack, the same room (a sub-room counts as its room, and
//            the other way round), or two rooms joined by a cable drop of
//            the matching kind, which the link then consumes. Devices with
//            numbered ports (switches, patch panels) take a port on each
//            such end; a port carries one link. Both record a termination;
//            fiber also records its mode, which must match the drop's.
//   mesh   – a wireless hop between an access point and an access point or
//            router at the same property. No ports, no drop.
//   vpn    – between gateways (routers, firewalls, modems, controllers) of
//            the same client, typically at two locations. No ports, no drop.
//   hdmi / sdi / audio / speaker / rs232 / ir / relay (ROADMAP J6) – the AV
//            and control wiring. Of the rules above only reach applies, and
//            only its weakest form: both devices at one property, since the
//            wire has to get there. No ports (an amplifier's outputs are not
//            the numbered ports this product counts), no termination, no
//            fiber mode, no drop — an AV run is its own cable, recorded on
//            the link. Both ends must be AV or control gear (AV_TYPES), and
//            two runs between the same pair are allowed, unlike mesh and
//            VPN, because a screen fed twice from one matrix is ordinary.

/** Where an item sits, plus the property it belongs to. */
export interface Placement {
  readonly propertyId: string;
  readonly roomId: string;
  readonly rackId: string | null;
}

export interface ClientIndex {
  readonly items: ReadonlyMap<string, Item>;
  readonly placements: ReadonlyMap<string, Placement>;
  readonly roomParents: ReadonlyMap<string, string | null>;
  readonly rackRooms: ReadonlyMap<string, string>;
  readonly roomNames: ReadonlyMap<string, string>;
  readonly rackNames: ReadonlyMap<string, string>;
  readonly propertyNames: ReadonlyMap<string, string>;
  readonly drops: readonly CableDrop[];
  /** The client's LAG and mirror groups (ROADMAP C5). */
  readonly portGroups: readonly PortGroup[];
  /** Children hanging off a device's port (ROADMAP C10), keyed `${itemId}:${port}`. */
  readonly portChildren: ReadonlyMap<string, readonly Item[]>;
  /** Children hanging off any port of a parent device, keyed by the parent's id. */
  readonly childrenByParent: ReadonlyMap<string, readonly Item[]>;
}

/** The key a child hanging off a device's port is filed under. */
function portKey(itemId: string, port: number): string {
  return `${itemId}:${String(port)}`;
}

/** Devices whose links land on numbered ports; a gateway only once its LAN ports are declared. */
/** The device types that carry numbered ports; a device of any other type has none. */
export const PORTED_TYPES = new Set<Item['type']>([
  'switch',
  'patch_panel',
  'wall_patch_panel',
  'outlet',
  'router',
  'firewall',
  'modem_ont',
  // A converter and a splitter are link participants (ROADMAP J5): a run lands
  // on a numbered port of theirs the way it lands on a panel's.
  'media_converter',
  'splitter',
]);
const GATEWAY_TYPES = new Set<Item['type']>(['router', 'firewall', 'modem_ont', 'controller']);
const WIRED_MEDIA = new Set<ConnectionMedium>(['cable', 'fiber']);
/** Devices with an SFP cage or fiber ports; everything else is copper only. */
const FIBER_CAPABLE = new Set<Item['type']>([
  'switch',
  'router',
  'firewall',
  'modem_ont',
  'controller',
  'server_nas',
  'patch_panel',
  'wall_patch_panel',
  // A converter is the device a fiber run lands on to become copper, and a
  // passive splitter is usually optical (ROADMAP J5).
  'media_converter',
  'splitter',
  'other',
]);

/** Everything the rules need, looked up once from a client tree. */
export function indexClient(tree: ClientTree): ClientIndex {
  const items = new Map<string, Item>();
  const placements = new Map<string, Placement>();
  const roomParents = new Map<string, string | null>();
  const rackRooms = new Map<string, string>();
  const roomNames = new Map<string, string>();
  const rackNames = new Map<string, string>();
  const propertyNames = new Map<string, string>();
  const drops: CableDrop[] = [];
  for (const property of tree.properties) {
    propertyNames.set(property.property.id, property.property.name);
  }
  walkProperties(tree.properties, (room, context) => {
    roomParents.set(room.room.id, context.parentId);
    roomNames.set(room.room.id, room.room.name);
    for (const rack of room.racks) {
      rackRooms.set(rack.rack.id, room.room.id);
      rackNames.set(rack.rack.id, rack.rack.name);
    }
    drops.push(...room.drops);
  });
  for (const placed of itemsIn(tree.properties.flatMap((property) => property.rooms))) {
    items.set(placed.item.id, placed.item);
  }
  for (const property of tree.properties) {
    for (const placed of itemsIn(property.rooms, property.property.id)) {
      placements.set(placed.item.id, {
        propertyId: placed.propertyId,
        roomId: placed.roomId,
        rackId: placed.rackId,
      });
    }
  }
  // Children hanging off another device's port (ROADMAP C10): a child records
  // the parent device and, when a port is chosen, which port; both indexes are
  // built so the trace, the faceplate, and the topology can find them.
  const portChildren = new Map<string, Item[]>();
  const childrenByParent = new Map<string, Item[]>();
  for (const item of items.values()) {
    if (item.portParentItemId === null) {
      continue;
    }
    const byParent = childrenByParent.get(item.portParentItemId) ?? [];
    byParent.push(item);
    childrenByParent.set(item.portParentItemId, byParent);
    if (item.portParentPort !== null) {
      const key = portKey(item.portParentItemId, item.portParentPort);
      const atPort = portChildren.get(key) ?? [];
      atPort.push(item);
      portChildren.set(key, atPort);
    }
  }
  return {
    items,
    placements,
    roomParents,
    rackRooms,
    roomNames,
    rackNames,
    propertyNames,
    drops,
    portGroups: tree.portGroups ?? [],
    portChildren,
    childrenByParent,
  };
}

/** The devices hanging off (`itemId`, `port`) as children (ROADMAP C10). */
export function childrenOffPort(index: ClientIndex, itemId: string, port: number): readonly Item[] {
  return index.portChildren.get(portKey(itemId, port)) ?? [];
}

/** Numbered ports a device offers, or null when it has none to allocate. */
export function portCapacity(item: Pick<Item, 'type' | 'details'>): number | null {
  if (!PORTED_TYPES.has(item.type)) {
    return null;
  }
  // A modular panel takes its capacity from its cassettes' ports together
  // (ROADMAP I3); a flat panel keeps its declared count, as before.
  const fromCassettes = panelCassetteCapacity(item.details);
  if (fromCassettes !== null) {
    return fromCassettes;
  }
  const declared = item.details['portCount'];
  if (typeof declared === 'number' && declared > 0) {
    return declared;
  }
  const ports = item.details['ports'];
  if (Array.isArray(ports) && ports.length > 0) {
    const numbers = ports.map((port: unknown) =>
      typeof port === 'object' && port !== null && 'number' in port ? Number(port.number) : 0,
    );
    return Math.max(...numbers);
  }
  return null;
}

/** The room a drop end sits in, when it is a node of the tree. */
function nodeRoom(index: ClientIndex, node: DropOrigin | null): string | null {
  if (node === null) {
    return null;
  }
  switch (node.type) {
    case 'room': {
      return node.id;
    }
    case 'rack': {
      return index.rackRooms.get(node.id) ?? null;
    }
    case 'item': {
      return index.placements.get(node.id)?.roomId ?? null;
    }
  }
}

/**
 * Every room a drop touches: its home room and the rooms of either
 * structured end. A run with a device at both ends spans the two devices'
 * rooms, which need not be the room the drop is filed under.
 */
function dropRooms(index: ClientIndex, drop: CableDrop): Set<string> {
  const rooms = new Set<string>([drop.roomId]);
  for (const node of [drop.fromNode, drop.toNode]) {
    const room = nodeRoom(index, node);
    if (room !== null) {
      rooms.add(room);
    }
  }
  return rooms;
}

/** Drops that join these two (distinct) rooms, at any pair of their ends. */
export function dropsBetween(index: ClientIndex, roomA: string, roomB: string): CableDrop[] {
  if (roomA === roomB) {
    return [];
  }
  return index.drops.filter((drop) => {
    const rooms = dropRooms(index, drop);
    return rooms.has(roomA) && rooms.has(roomB);
  });
}

function sameOrNested(index: ClientIndex, roomA: string, roomB: string): boolean {
  return (
    roomA === roomB ||
    index.roomParents.get(roomA) === roomB ||
    index.roomParents.get(roomB) === roomA
  );
}

/** Whether a drop can carry a link of this medium: fiber on fiber, copper on the rest. */
export function dropCarries(drop: CableDrop, medium: ConnectionMedium): boolean {
  const fiber = fiberModeOf(drop.cableType) !== null;
  return medium === 'fiber' ? fiber : !fiber;
}

function takesFiber(index: ClientIndex, itemId: string): boolean {
  const item = index.items.get(itemId);
  return item !== undefined && FIBER_CAPABLE.has(item.type);
}

export type Reach =
  | { readonly via: 'rack' }
  | { readonly via: 'room' }
  | { readonly via: 'run'; readonly drops: readonly CableDrop[] };

/** How a wire of this medium could join two devices, or null when out of reach. */
export function wiredReach(
  index: ClientIndex,
  medium: ConnectionMedium,
  fromId: string,
  toId: string,
): Reach | null {
  const from = index.placements.get(fromId);
  const to = index.placements.get(toId);
  if (from === undefined || to === undefined) {
    return null;
  }
  if (from.propertyId !== to.propertyId) {
    return null;
  }
  if (medium === 'fiber' && !(takesFiber(index, fromId) && takesFiber(index, toId))) {
    return null;
  }
  if (from.rackId !== null && from.rackId === to.rackId) {
    return { via: 'rack' };
  }
  if (sameOrNested(index, from.roomId, to.roomId)) {
    return { via: 'room' };
  }
  const drops = dropsBetween(index, from.roomId, to.roomId).filter((drop) =>
    dropCarries(drop, medium),
  );
  return drops.length === 0 ? null : { via: 'run', drops };
}

export function canMesh(index: ClientIndex, fromId: string, toId: string): boolean {
  const from = index.items.get(fromId);
  const to = index.items.get(toId);
  const fromPlace = index.placements.get(fromId);
  const toPlace = index.placements.get(toId);
  return (
    from !== undefined &&
    to !== undefined &&
    fromPlace?.propertyId === toPlace?.propertyId &&
    hasWifi(from.type, from.details) &&
    hasWifi(to.type, to.details) &&
    (from.type === 'access_point' || to.type === 'access_point')
  );
}

/**
 * Devices an AV or control run reaches (ROADMAP J6): the picture, sound, and
 * automation gear, the controller and receiver that drive them, the computer
 * or laptop that feeds a screen, and `other`, which is where anything the
 * registry does not name is filed. A switch is deliberately not here: an
 * HDMI cable does not plug into one.
 */
const AV_TYPES = new Set<Item['type']>([
  'screen',
  'projector',
  'amplifier',
  'receiver',
  'speaker',
  'keypad',
  'thermostat',
  'sensor',
  'controller',
  'computer',
  'laptop',
  // Security and life safety (ROADMAP J7) is control wiring too: a fire panel
  // reaches an access control panel by relay, and a reader its panel by
  // RS-232 or its own bus, which is what these media are for.
  'access_control_panel',
  'reader',
  'door_strike',
  'intrusion_panel',
  'fire_panel',
  'horn_strobe',
  'other',
]);

/** Whether an AV or control run could join these two: both AV gear, both at one property. */
export function canAv(index: ClientIndex, fromId: string, toId: string): boolean {
  const from = index.items.get(fromId);
  const to = index.items.get(toId);
  return (
    from !== undefined &&
    to !== undefined &&
    AV_TYPES.has(from.type) &&
    AV_TYPES.has(to.type) &&
    index.placements.get(fromId)?.propertyId === index.placements.get(toId)?.propertyId
  );
}

export function canVpn(index: ClientIndex, fromId: string, toId: string): boolean {
  const from = index.items.get(fromId);
  const to = index.items.get(toId);
  return (
    from !== undefined &&
    to !== undefined &&
    GATEWAY_TYPES.has(from.type) &&
    GATEWAY_TYPES.has(to.type)
  );
}

/** A port of an item taken by a run landing on it, at either end of the run. */
export interface DropLanding {
  readonly dropId: string;
  readonly port: number;
}

/** Ports of an item taken by cable drops landing on it, from either end of the run. */
export function portsTakenByDrops(drops: readonly CableDrop[], itemId: string): DropLanding[] {
  const taken: DropLanding[] = [];
  for (const drop of drops) {
    for (const end of [drop.fromNode, drop.toNode]) {
      if (end?.type === 'item' && end.id === itemId && end.port !== null) {
        taken.push({ dropId: drop.id, port: end.port });
      }
    }
  }
  return taken;
}

/**
 * Ports of an item already carrying a link or a run. A run's port stays
 * available to the one link that travels over that run (`viaDropId`).
 */
/**
 * A patch panel port has two sides: a run from another room is punched
 * down at the rear (a drop landing, or a link that travels over a drop) and
 * a patch cord plugs into the front (a link with no drop). Every other
 * device has one jack per port.
 */
export type PortSide = 'front' | 'rear';
/**
 * How a panel's two sides are named for people: A faces the devices and the
 * uplink (patch cables), B is where the runs land, one run per port.
 */
export const PORT_SIDE_LABELS: Readonly<Record<PortSide, string>> = {
  front: 'A side',
  rear: 'B side',
};

export function sideOf(connection: Pick<Connection, 'dropId'>): PortSide {
  return connection.dropId === null ? 'front' : 'rear';
}

function isPanel(index: ClientIndex, itemId: string): boolean {
  const type = index.items.get(itemId)?.type;
  return type !== undefined && isPanelType(type);
}

/** How many positions (`itemId`, `port`) fans out to (ROADMAP I2); 1 for a plain jack. */
export function positionsOfPort(index: ClientIndex, itemId: string, port: number): number {
  const ports = index.items.get(itemId)?.details['ports'];
  if (!isPortList(ports)) {
    return 1;
  }
  const row = ports.find((entry) => entry.number === port);
  return row === undefined ? 1 : portPositions(row);
}

/**
 * The positions taken on each port of `itemId`, on the side a new link on
 * `side` would use. A link with no position of its own (a plain link on a
 * single-position port) counts as position 1, so a plain port reads as "the
 * one position taken"; a bare drop landing likewise takes the first position.
 * The drop `viaDropId` is the run the new link travels over, so its landing is
 * not held against it (it is exactly the run the link consumes).
 */
function positionsByPort(
  index: ClientIndex,
  connections: readonly Connection[],
  itemId: string,
  viaDropId: string | null,
  side: PortSide | null,
): Map<number, Set<number>> {
  const panel = isPanel(index, itemId);
  const wanted = side ?? (viaDropId === null ? 'front' : 'rear');
  const byPort = new Map<number, Set<number>>();
  const note = (port: number, position: number): void => {
    const taken = byPort.get(port) ?? new Set<number>();
    taken.add(position);
    byPort.set(port, taken);
  };
  for (const connection of connections) {
    if (panel && sideOf(connection) !== wanted) {
      continue;
    }
    for (const end of [connection.a, connection.b]) {
      if (end.itemId === itemId && end.port !== null) {
        note(end.port, end.position ?? 1);
      }
    }
  }
  if (!panel || wanted === 'rear') {
    for (const landing of portsTakenByDrops(index.drops, itemId)) {
      if (landing.dropId !== viaDropId) {
        note(landing.port, 1);
      }
    }
  }
  return byPort;
}

/**
 * The ports of `itemId` a new link on `side` cannot take. On a panel only
 * the same side counts; elsewhere every link and landing does. A
 * multi-position port stays available while any position is free (ROADMAP
 * I2), so it is "used" only once every position is taken. The drop
 * `viaDropId` is the run the new link travels over, so its landing port is
 * not held against it (it is exactly the port to use).
 */
export function usedPorts(
  index: ClientIndex,
  connections: readonly Connection[],
  itemId: string,
  viaDropId: string | null = null,
  side: PortSide | null = null,
): Set<number> {
  const byPort = positionsByPort(index, connections, itemId, viaDropId, side);
  const used = new Set<number>();
  for (const [port, taken] of byPort) {
    if (taken.size >= positionsOfPort(index, itemId, port)) {
      used.add(port);
    }
  }
  return used;
}

/** The positions of (`itemId`, `port`) already taken on the side a new link would use (ROADMAP I2). */
export function usedPositions(
  index: ClientIndex,
  connections: readonly Connection[],
  itemId: string,
  port: number,
  viaDropId: string | null = null,
  side: PortSide | null = null,
): Set<number> {
  return (
    positionsByPort(index, connections, itemId, viaDropId, side).get(port) ?? new Set<number>()
  );
}

/**
 * The free legs of a multi-position port (ROADMAP I2), for the link form to
 * offer; empty for a plain single-position port, which takes no position.
 */
export function freePositions(
  index: ClientIndex,
  connections: readonly Connection[],
  itemId: string,
  port: number,
  viaDropId: string | null = null,
  side: PortSide | null = null,
): number[] {
  const count = positionsOfPort(index, itemId, port);
  if (count <= 1) {
    return [];
  }
  const used = usedPositions(index, connections, itemId, port, viaDropId, side);
  return Array.from({ length: count }, (_, at) => at + 1).filter((position) => !used.has(position));
}

/** Every port of `itemId` that holds anything on either side. */
export function occupiedPorts(
  index: ClientIndex,
  connections: readonly Connection[],
  itemId: string,
): Set<number> {
  return new Set([
    ...usedPorts(index, connections, itemId, null, 'front'),
    ...usedPorts(index, connections, itemId, null, 'rear'),
  ]);
}

/** Whether a link could still land on `item`: no ports at all, or a free one on some side. */
export function hasFreePort(
  index: ClientIndex,
  item: Item,
  connections: readonly Connection[],
): boolean {
  const capacity = portCapacity(item);
  if (capacity === null) {
    return true;
  }
  if (freePorts(index, item, connections).length > 0) {
    return true;
  }
  return isPanel(index, item.id) && freePorts(index, item, connections, null, 'rear').length > 0;
}

export function freePorts(
  index: ClientIndex,
  item: Item,
  connections: readonly Connection[],
  viaDropId: string | null = null,
  side: PortSide | null = null,
): number[] {
  const capacity = portCapacity(item);
  if (capacity === null) {
    return [];
  }
  const used = usedPorts(index, connections, item.id, viaDropId, side);
  return Array.from({ length: capacity }, (_, index) => index + 1).filter(
    (port) => !used.has(port),
  );
}

export function usedDropIds(connections: readonly Connection[]): Set<string> {
  return new Set(
    connections.flatMap((connection) => (connection.dropId === null ? [] : [connection.dropId])),
  );
}

export interface Candidate {
  readonly item: Item;
  readonly medium: ConnectionMedium;
  readonly reach: Reach | null;
  /** For a run: the drops between the rooms not yet carrying a link. */
  readonly freeDrops: readonly CableDrop[];
  /** Where the device is, for the picker: "Rack 1", "Reception", "Warehouse · Office". */
  readonly place: string;
}

/** "Rack 1", "Reception", "Warehouse · Office": where a device sits, seen from `fromId`. */
export function placeLabel(index: ClientIndex, itemId: string, fromId: string): string {
  const place = index.placements.get(itemId);
  const from = index.placements.get(fromId);
  if (place === undefined) {
    return '';
  }
  const parts: string[] = [];
  if (place.propertyId !== from?.propertyId) {
    parts.push(index.propertyNames.get(place.propertyId) ?? '');
  }
  parts.push(index.roomNames.get(place.roomId) ?? '');
  if (place.rackId !== null) {
    parts.push(index.rackNames.get(place.rackId) ?? '');
  }
  return parts.filter((part) => part !== '').join(' · ');
}

function alreadyLinked(
  connections: readonly Connection[],
  fromId: string,
  toId: string,
  medium: ConnectionMedium,
): boolean {
  return connections.some(
    (connection) =>
      connection.medium === medium &&
      ((connection.a.itemId === fromId && connection.b.itemId === toId) ||
        (connection.a.itemId === toId && connection.b.itemId === fromId)),
  );
}

const REACH_RANK = { rack: 0, room: 1, run: 2 } as const;

function wiredCandidates(
  index: ClientIndex,
  connections: readonly Connection[],
  fromId: string,
  medium: ConnectionMedium,
): Candidate[] {
  const fromItem = index.items.get(fromId);
  if (fromItem === undefined) {
    return [];
  }
  if (!hasFreePort(index, fromItem, connections)) {
    return [];
  }
  const used = usedDropIds(connections);
  const out: Candidate[] = [];
  for (const item of index.items.values()) {
    if (item.id === fromId) {
      continue;
    }
    const reach = wiredReach(index, medium, fromId, item.id);
    if (reach === null) {
      continue;
    }
    if (!hasFreePort(index, item, connections)) {
      continue;
    }
    // A free run between the rooms that does not land by port on some third device.
    const freeDrops =
      reach.via === 'run'
        ? reach.drops.filter((drop) => !used.has(drop.id) && landsOnEnds(drop, fromId, item.id))
        : [];
    if (reach.via === 'run' && freeDrops.length === 0) {
      continue;
    }
    out.push({ item, medium, reach, freeDrops, place: placeLabel(index, item.id, fromId) });
  }
  return out.toSorted(
    (left, right) =>
      REACH_RANK[left.reach?.via ?? 'run'] - REACH_RANK[right.reach?.via ?? 'run'] ||
      left.item.name.localeCompare(right.item.name),
  );
}

function wirelessCandidates(
  index: ClientIndex,
  connections: readonly Connection[],
  fromId: string,
): Candidate[] {
  const out: Candidate[] = [];
  for (const item of index.items.values()) {
    if (item.id === fromId) {
      continue;
    }
    const place = placeLabel(index, item.id, fromId);
    if (canMesh(index, fromId, item.id) && !alreadyLinked(connections, fromId, item.id, 'mesh')) {
      out.push({ item, medium: 'mesh', reach: null, freeDrops: [], place });
    }
    if (canVpn(index, fromId, item.id) && !alreadyLinked(connections, fromId, item.id, 'vpn')) {
      out.push({ item, medium: 'vpn', reach: null, freeDrops: [], place });
    }
  }
  return out.toSorted(
    (left, right) =>
      left.medium.localeCompare(right.medium) || left.item.name.localeCompare(right.item.name),
  );
}

/**
 * The AV and control peers of a device (ROADMAP J6), one candidate per
 * medium: only AV gear at the same property, and only when the device itself
 * is AV gear, so the picker on a switch is not buried under seven media it
 * can never carry.
 */
function avCandidates(index: ClientIndex, fromId: string): Candidate[] {
  const out: Candidate[] = [];
  for (const item of index.items.values()) {
    if (item.id === fromId || !canAv(index, fromId, item.id)) {
      continue;
    }
    const place = placeLabel(index, item.id, fromId);
    for (const medium of AV_MEDIA) {
      out.push({ item, medium, reach: null, freeDrops: [], place });
    }
  }
  return out.toSorted(
    (left, right) =>
      left.item.name.localeCompare(right.item.name) ||
      AV_MEDIA.indexOf(left.medium as AvMedium) - AV_MEDIA.indexOf(right.medium as AvMedium),
  );
}

/**
 * Every device the given one could link to, by medium, in a stable order:
 * cable (same rack, same room, then reached by a free copper run), fiber
 * (likewise over fiber runs), then mesh and VPN peers, then the AV and
 * control peers (ROADMAP J6).
 */
export function connectionCandidates(
  index: ClientIndex,
  connections: readonly Connection[],
  fromId: string,
): Candidate[] {
  return [
    ...wiredCandidates(index, connections, fromId, 'cable'),
    ...wiredCandidates(index, connections, fromId, 'fiber'),
    ...wirelessCandidates(index, connections, fromId),
    ...avCandidates(index, fromId),
  ];
}

function checkPort(
  index: ClientIndex,
  connections: readonly Connection[],
  end: ConnectionInput['a'],
  medium: ConnectionMedium,
  viaDropId: string | null,
): string | null {
  const item = index.items.get(end.itemId);
  if (item === undefined) {
    return 'One of the devices does not exist at this client';
  }
  const capacity = portCapacity(item);
  const position = end.position ?? null;
  if (!WIRED_MEDIA.has(medium)) {
    if (end.port !== null) {
      return `${item.name} takes no port on a ${medium} link`;
    }
    return position === null ? null : `${item.name} takes no position on a ${medium} link`;
  }
  if (capacity === null) {
    return end.port === null ? null : `${item.name} has no numbered ports`;
  }
  if (end.port === null) {
    return `Choose a port on ${item.name}`;
  }
  if (end.port > capacity) {
    return `${item.name} has only ${String(capacity)} ports`;
  }
  // A multi-position port (an MPO/breakout, ROADMAP I2) is patched one leg at a
  // time: a link must name a free position; a plain port takes no position.
  const positions = positionsOfPort(index, item.id, end.port);
  if (positions > 1) {
    if (position === null) {
      return `Choose a position on ${item.name} port ${String(end.port)} (1–${String(positions)})`;
    }
    if (position > positions) {
      return `${item.name} port ${String(end.port)} has only ${String(positions)} positions`;
    }
    if (usedPositions(index, connections, item.id, end.port, viaDropId).has(position)) {
      return `Position ${String(position)} on ${item.name} port ${String(end.port)} is already in use`;
    }
    return null;
  }
  if (position !== null) {
    return `${item.name} port ${String(end.port)} has no positions`;
  }
  if (usedPorts(index, connections, item.id, viaDropId).has(end.port)) {
    return `Port ${String(end.port)} on ${item.name} is already in use`;
  }
  return null;
}

/**
 * The devices a run lands on by port (a panel's B side, a switch port),
 * which a link over the run must start or end at: the run is consumed at
 * that port, and the rest of the route (a patch on the panel's A side)
 * is its own link.
 */
function landingsOf(drop: CableDrop): { readonly id: string; readonly port: number }[] {
  return [drop.fromNode, drop.toNode].flatMap((end) =>
    end?.type === 'item' && end.port !== null ? [{ id: end.id, port: end.port }] : [],
  );
}

/** Whether every device the run lands on by port is one of the two link ends. */
export function landsOnEnds(drop: CableDrop, a: string, b: string): boolean {
  return landingsOf(drop).every((landing) => landing.id === a || landing.id === b);
}

/** Whether the run's own two ends are the two devices of `input`, either way round. */
function runsBetween(drop: CableDrop, input: ConnectionInput): boolean {
  const ends = [drop.fromNode, drop.toNode].map((end) => (end?.type === 'item' ? end.id : null));
  return (
    (ends[0] === input.a.itemId && ends[1] === input.b.itemId) ||
    (ends[0] === input.b.itemId && ends[1] === input.a.itemId)
  );
}

function checkWired(
  index: ClientIndex,
  connections: readonly Connection[],
  input: ConnectionInput,
): string | null {
  const label = CONNECTION_MEDIUM_LABELS[input.medium].toLowerCase();
  const allowed = input.medium === 'fiber' ? FIBER_TERMINATIONS : COPPER_TERMINATIONS;
  if (input.termination === null || !allowed.includes(input.termination)) {
    return `Choose how the ${label} is terminated (${allowed
      .map((entry) => LINK_TERMINATION_LABELS[entry])
      .join(' or ')})`;
  }
  if (input.medium === 'fiber' && input.fiberMode === null) {
    return 'Choose single-mode or multi-mode for the fiber';
  }
  if (input.medium === 'cable' && input.fiberMode !== null) {
    return 'An ethernet cable has no fiber mode';
  }
  const reach = wiredReach(index, input.medium, input.a.itemId, input.b.itemId);
  if (reach === null) {
    return `These devices are not within reach of a ${label}: same rack, same room, or rooms joined by a matching cable drop`;
  }
  if (reach.via !== 'run' && input.dropId === null) {
    return null;
  }
  if (input.dropId === null) {
    return 'Choose the cable drop that carries this link between the rooms';
  }
  // Between rooms the drop must be one that joins them; within a room or a
  // rack a run is still a cable (a jack to a desk, a wall panel to a switch),
  // so the drop must be the one whose own two ends are these devices.
  const dropId = input.dropId;
  const drop =
    reach.via === 'run'
      ? reach.drops.find((entry) => entry.id === dropId)
      : index.drops.find(
          (entry) =>
            entry.id === dropId && runsBetween(entry, input) && dropCarries(entry, input.medium),
        );
  if (drop === undefined) {
    return reach.via === 'run'
      ? `That cable drop does not join these two rooms with ${label}`
      : `That cable drop does not run between these two devices with ${label}`;
  }
  if (input.medium === 'fiber' && fiberModeOf(drop.cableType) !== input.fiberMode) {
    return `That drop is ${FIBER_MODE_LABELS[fiberModeOf(drop.cableType) ?? 'multi_mode'].toLowerCase()} fiber`;
  }
  if (usedDropIds(connections).has(input.dropId)) {
    return 'That cable drop already carries a link';
  }
  for (const landing of landingsOf(drop)) {
    const end = [input.a, input.b].find((entry) => entry.itemId === landing.id);
    if (end === undefined) {
      const name = index.items.get(landing.id)?.name ?? 'a device';
      return `${drop.label} lands on ${name} port ${String(landing.port)}: link from that port, and patch the rest of the route from ${name} separately`;
    }
    if (end.port !== landing.port) {
      return `${drop.label} lands on port ${String(landing.port)}; use that port`;
    }
  }
  return null;
}

/** The connector recorded on a device's numbered port, or undefined when none is. */
function portConnector(
  index: ClientIndex,
  itemId: string,
  port: number | null,
): PortConnector | undefined {
  if (port === null) {
    return undefined;
  }
  const ports = index.items.get(itemId)?.details['ports'];
  return isPortList(ports) ? ports.find((entry) => entry.number === port)?.connector : undefined;
}

/**
 * Whether a device bridges media without having said how: a media converter,
 * splitter, or modem that declares neither of its two connector sides
 * (ROADMAP J5). Its links go unjudged, since a device that changes medium on
 * purpose and has not said between what cannot be held to a mating rule.
 */
function bridgesUndeclared(item: Item): boolean {
  return isMediaConverter(item.type) && !declaresConverterSides(item.details);
}

/**
 * The connector one end of a link meets. On a converter or splitter that has
 * declared its sides (ROADMAP J5) that is the side's own connector — port 1
 * is the A side, every higher port a leg of the B side — since the two sides
 * differ by design and a port row would only repeat it. Everywhere else it is
 * the connector recorded on the port itself (ROADMAP B2).
 */
function endConnector(
  index: ClientIndex,
  item: Item,
  port: number | null,
): PortConnector | undefined {
  if (isMediaConverter(item.type) && port !== null) {
    const side = converterConnectorAt(item.details, port);
    if (side !== null) {
      return side;
    }
  }
  return portConnector(index, item.id, port);
}

/**
 * The reason two ports' connectors cannot be joined (ROADMAP B3), or null
 * when they can or the check does not apply. It applies only when both ends
 * present a connector and neither device bridges media without saying how;
 * RJ45↔LC (copper↔fiber) is the case it refuses, while an SFP cage mates with
 * either side. A converter that has declared its sides is judged like any
 * other device — what plugs into each side must mate with that side — which
 * is the whole point of declaring them (ROADMAP J5).
 */
function connectorProblem(index: ClientIndex, input: ConnectionInput): string | null {
  const a = index.items.get(input.a.itemId);
  const b = index.items.get(input.b.itemId);
  if (a === undefined || b === undefined) {
    return null;
  }
  if (bridgesUndeclared(a) || bridgesUndeclared(b)) {
    return null;
  }
  const ca = endConnector(index, a, input.a.port);
  const cb = endConnector(index, b, input.b.port);
  if (ca === undefined || cb === undefined || connectorsMate(ca, cb)) {
    return null;
  }
  return `${a.name} port ${String(input.a.port)} is ${ca} and ${b.name} port ${String(input.b.port)} is ${cb}: those connectors do not mate. Put a media converter between them.`;
}

/** The index as it would be with `itemId` placed elsewhere: a move, previewed. */
export function movedIndex(index: ClientIndex, itemId: string, placement: Placement): ClientIndex {
  const placements = new Map(index.placements);
  placements.set(itemId, placement);
  return { ...index, placements };
}

/** The links of `itemId` that this index no longer allows (after a move, say). */
export function brokenLinks(
  index: ClientIndex,
  connections: readonly Connection[],
  itemId: string,
): Connection[] {
  return connections.filter((connection) => {
    if (connection.a.itemId !== itemId && connection.b.itemId !== itemId) {
      return false;
    }
    const others = connections.filter((entry) => entry.id !== connection.id);
    const { id: _id, clientId: _clientId, createdAt: _c, updatedAt: _u, ...input } = connection;
    return validateConnection(index, others, input) !== null;
  });
}

/** The reason a link cannot be made, or null when it can. */
export function validateConnection(
  index: ClientIndex,
  connections: readonly Connection[],
  input: ConnectionInput,
): string | null {
  if (input.a.itemId === input.b.itemId) {
    return 'A device cannot be linked to itself';
  }
  const portProblem =
    checkPort(index, connections, input.a, input.medium, input.dropId) ??
    checkPort(index, connections, input.b, input.medium, input.dropId);
  if (portProblem !== null) {
    return portProblem;
  }
  switch (input.medium) {
    case 'cable':
    case 'fiber': {
      return checkWired(index, connections, input) ?? connectorProblem(index, input);
    }
    case 'mesh': {
      if (!canMesh(index, input.a.itemId, input.b.itemId)) {
        return 'A mesh link joins an access point with an access point or router at the same property';
      }
      if (input.dropId !== null || input.termination !== null || input.fiberMode !== null) {
        return 'A mesh link uses no cable drop, termination, or fiber mode';
      }
      return alreadyLinked(connections, input.a.itemId, input.b.itemId, 'mesh')
        ? 'These two are already mesh-linked'
        : null;
    }
    case 'vpn': {
      if (!canVpn(index, input.a.itemId, input.b.itemId)) {
        return 'A VPN link joins two gateways (router, firewall, modem, or controller)';
      }
      if (input.dropId !== null || input.termination !== null || input.fiberMode !== null) {
        return 'A VPN link uses no cable drop, termination, or fiber mode';
      }
      return alreadyLinked(connections, input.a.itemId, input.b.itemId, 'vpn')
        ? 'These two are already VPN-linked'
        : null;
    }
    case 'hdmi':
    case 'sdi':
    case 'audio':
    case 'speaker':
    case 'rs232':
    case 'ir':
    case 'relay': {
      // The AV and control media (ROADMAP J6), each named rather than caught
      // by a default, so adding an eighth is a compile error here rather than
      // a silent reuse of these rules. Only the reach rule of a wired link
      // applies: the cable has to get there, so both devices stand at one
      // property. Nothing else does
      // — see AV_MEDIA in shared/schemas/connection.ts — and `checkPort`
      // above has already refused a port or a leg on a medium that takes
      // none. Two of these are not counted as a duplicate the way a mesh or
      // VPN pair is: a screen fed by two HDMI runs from the same matrix is
      // an ordinary install.
      if (!canAv(index, input.a.itemId, input.b.itemId)) {
        return `A ${CONNECTION_MEDIUM_LABELS[input.medium]} link joins two AV or control devices at the same property`;
      }
      if (input.dropId !== null || input.termination !== null || input.fiberMode !== null) {
        return `A ${CONNECTION_MEDIUM_LABELS[input.medium]} link uses no cable drop, termination, or fiber mode`;
      }
      return null;
    }
  }
}

/**
 * What a link is made of, resolved to one place. A link carried over a cable
 * run reads the cable off that run (the run is the cable, and stays the one
 * record of it); a direct link — a patch cord, a jumper between panels —
 * reads its own fields. `fromRun` says which, so a caller can tell a run's
 * verdict from one recorded on the link itself.
 */
export interface LinkCable {
  readonly label: string;
  readonly cableType: CableType | null;
  readonly color: DropColor | null;
  readonly lengthValue: number | null;
  readonly lengthUnit: LengthUnit;
  readonly status: LinkStatus;
  readonly test: CableTest;
  readonly fromRun: boolean;
}

/** The run a link travels over, if any and still present. */
export function linkDrop(
  index: ClientIndex,
  connection: Pick<Connection, 'dropId'>,
): CableDrop | undefined {
  return connection.dropId === null
    ? undefined
    : index.drops.find((entry) => entry.id === connection.dropId);
}

/** Resolve a link's cable facts, from its run when it has one, else from itself. */
export function linkCable(index: ClientIndex, connection: Connection): LinkCable {
  const drop = linkDrop(index, connection);
  if (drop !== undefined) {
    return {
      label: connection.label === '' ? drop.label : connection.label,
      cableType: drop.cableType,
      color: drop.color,
      lengthValue: drop.lengthValue,
      lengthUnit: drop.lengthUnit,
      status: connection.linkStatus,
      test: {
        testedOn: drop.testedOn,
        testResult: drop.testResult,
        measuredLength: drop.measuredLength,
        lengthUnit: drop.lengthUnit,
      },
      fromRun: true,
    };
  }
  return {
    label: connection.label,
    cableType: connection.cableType,
    color: connection.color,
    lengthValue: connection.lengthValue,
    lengthUnit: connection.lengthUnit,
    status: connection.linkStatus,
    test: {
      testedOn: connection.testedOn,
      testResult: connection.testResult,
      measuredLength: null,
      lengthUnit: connection.lengthUnit,
    },
    fromRun: false,
  };
}

/** "60 ft" from a length and its unit, or '' when no length is known. */
export function lengthText(value: number | null, unit: LengthUnit): string {
  return value === null ? '' : `${String(value)} ${unit}`;
}

export interface ConnectionView {
  readonly id: string;
  readonly medium: ConnectionMedium;
  readonly mediumLabel: string;
  readonly localPort: number | null;
  readonly otherId: string;
  readonly otherName: string;
  readonly otherPort: number | null;
  readonly otherPlace: string;
  readonly dropLabel: string | null;
  readonly detail: string;
  /** The link's cable ID or circuit label; '' when unlabelled. */
  readonly label: string;
  /** "Cat6", "Fiber, single-mode (OS2)"; '' when the cable is unrecorded. */
  readonly cableTypeLabel: string;
  /** The link's chosen colour (ROADMAP A7), resolved from its run when it has one; null when none. */
  readonly color: DropColor | null;
  readonly lengthText: string;
  /** Whether the run is longer than its cable type's recommended maximum (ROADMAP A13). */
  readonly overLength: boolean;
  /** This device's port role in a LAG or mirror (ROADMAP C5), e.g. "LAG ag0"; '' when in none. */
  readonly groupNote: string;
  /** The life-cycle status, shown unless it is the plain default. */
  readonly statusLabel: string;
  readonly status: LinkStatus;
  /** "Tested 2026-09-01 · Pass · measured 42 ft", or '' when untested. */
  readonly testText: string;
  readonly notes: string;
}

/** A link as seen from one of its ends, for lists and the report. */
export function viewConnection(
  index: ClientIndex,
  connection: Connection,
  fromId: string,
): ConnectionView {
  const local = connection.a.itemId === fromId ? connection.a : connection.b;
  const other = connection.a.itemId === fromId ? connection.b : connection.a;
  const drop = linkDrop(index, connection);
  const cable = linkCable(index, connection);
  const detailParts = [
    connection.fiberMode === null ? '' : FIBER_MODE_LABELS[connection.fiberMode],
    connection.termination === null ? '' : LINK_TERMINATION_LABELS[connection.termination],
  ].filter((part) => part !== '');
  return {
    id: connection.id,
    medium: connection.medium,
    mediumLabel: CONNECTION_MEDIUM_LABELS[connection.medium],
    localPort: local.port,
    otherId: other.itemId,
    otherName: index.items.get(other.itemId)?.name ?? 'Removed device',
    otherPort: other.port,
    otherPlace: placeLabel(index, other.itemId, fromId),
    dropLabel: connection.dropId === null ? null : (drop?.label ?? 'Removed drop'),
    detail: detailParts.join(', '),
    // The link's own label; a run-carried link shows the run's label as
    // "via …" instead, so this stays blank and does not double it.
    label: connection.label,
    cableTypeLabel: cable.cableType === null ? '' : CABLE_TYPE_LABELS[cable.cableType],
    color: cable.color,
    lengthText: lengthText(cable.lengthValue, cable.lengthUnit),
    overLength:
      cable.cableType !== null &&
      isOverLength(cable.cableType, cable.lengthValue, cable.lengthUnit),
    groupNote: local.port === null ? '' : portGroupRole(index.portGroups, local.itemId, local.port),
    statusLabel: cable.status === DEFAULT_LINK_STATUS ? '' : LINK_STATUS_LABELS[cable.status],
    status: cable.status,
    testText: describeCableTest(cable.test),
    notes: connection.notes,
  };
}

/**
 * "PP1-12 · Port 3 → Panel A port 12 · Fiber, Single-mode, LC · Planned ·
 * via Drop 1 · Rack 1": the link on one line — its own label first (a
 * run-carried link shows the run as "via …" instead), then ports, the
 * medium and termination, its status unless it is the plain default, the run
 * it travels over, and where the far end sits. The cable it is made of and
 * its test verdict are the second line (describeLinkCable), not this one, so
 * a fiber link does not say "Fiber" twice.
 */
export function describeConnection(view: ConnectionView): string {
  const local = view.localPort === null ? '' : `Port ${String(view.localPort)} → `;
  const other =
    view.otherPort === null ? view.otherName : `${view.otherName} port ${String(view.otherPort)}`;
  const head = view.label === '' ? '' : `${view.label} · `;
  const parts = [`${head}${local}${other}`];
  const how = [view.medium === 'cable' ? '' : view.mediumLabel, view.detail]
    .filter((part) => part !== '')
    .join(', ');
  for (const part of [how, view.statusLabel]) {
    if (part !== '') {
      parts.push(part);
    }
  }
  if (view.dropLabel !== null) {
    parts.push(`via ${view.dropLabel}`);
  }
  if (view.otherPlace !== '') {
    parts.push(view.otherPlace);
  }
  return parts.join(' · ');
}

/** "Cat6, 60 ft · Tested 2026-09-01 · Pass": what the link is made of and its verdict; '' when neither is recorded. */
export function describeLinkCable(view: ConnectionView): string {
  const cable = [view.cableTypeLabel, view.lengthText].filter((part) => part !== '').join(', ');
  return [cable, view.testText].filter((part) => part !== '').join(' · ');
}

// --- Tracing ---------------------------------------------------------------

export interface TraceHop {
  /** '' for a run that ends unpatched at a drop. */
  readonly itemId: string;
  readonly itemName: string;
  readonly port: number | null;
  /** The position within a multi-position port this hop lands on (ROADMAP I2); null for a plain port. */
  readonly position?: number | null;
  /** On a panel, the side this hop arrives at or leaves from. */
  readonly side: PortSide | null;
  /** The drop a run travelled over to get here, if any. */
  readonly dropLabel: string | null;
  /** The room a run leads to when nothing is patched at its far end. */
  readonly roomName: string | null;
}

/** A child device hanging off a port (ROADMAP C10), as a trace hop. */
function childHop(child: Item): TraceHop {
  return {
    itemId: child.id,
    itemName: child.name,
    port: null,
    side: null,
    dropLabel: null,
    roomName: null,
  };
}

/** Every device hanging off any port of `itemId`, and everything behind them. */
function descendantHops(index: ClientIndex, itemId: string, visitedItems: Set<string>): TraceHop[] {
  const hops: TraceHop[] = [];
  for (const child of index.childrenByParent.get(itemId) ?? []) {
    if (visitedItems.has(child.id)) {
      continue;
    }
    visitedItems.add(child.id);
    hops.push(childHop(child), ...descendantHops(index, child.id, visitedItems));
  }
  return hops;
}

/** The devices hanging off (`itemId`, `port`) and their descendants (ROADMAP C10). */
function portChildHops(
  index: ClientIndex,
  itemId: string,
  port: number,
  visitedItems: Set<string>,
): TraceHop[] {
  const hops: TraceHop[] = [];
  for (const child of index.portChildren.get(portKey(itemId, port)) ?? []) {
    if (visitedItems.has(child.id)) {
      continue;
    }
    visitedItems.add(child.id);
    hops.push(childHop(child), ...descendantHops(index, child.id, visitedItems));
  }
  return hops;
}

function linkOn(
  connections: readonly Connection[],
  itemId: string,
  port: number,
  side: PortSide | null,
  position: number | null,
  visited: Set<string>,
): Connection | undefined {
  return connections.find(
    (connection) =>
      !visited.has(connection.id) &&
      (side === null || sideOf(connection) === side) &&
      [connection.a, connection.b].some(
        // A breakout port is patched per position (ROADMAP I2): only the leg
        // the trace is following continues; a plain port carries a null position.
        (end) => end.itemId === itemId && end.port === port && (end.position ?? null) === position,
      ),
  );
}

function otherEnd(connection: Connection, itemId: string): Connection['a'] {
  return connection.a.itemId === itemId ? connection.b : connection.a;
}

/** Whether a device passes a trace through itself, changing medium on the way (ROADMAP J5). */
function isConverter(index: ClientIndex, itemId: string): boolean {
  const type = index.items.get(itemId)?.type;
  return type !== undefined && isMediaConverter(type);
}

/**
 * The port a trace leaves a media converter or splitter by (ROADMAP J5): the
 * lowest port on the device's other side carrying a link the trace has not
 * already followed, or null when that side is unpatched. A splitter with
 * several legs on that side passes the trace out of the first; each other leg
 * is traced from its own port, since one path cannot show a fan-out.
 */
function converterExit(
  index: ClientIndex,
  connections: readonly Connection[],
  itemId: string,
  arrivedOn: number,
  visited: Set<string>,
): number | null {
  const item = index.items.get(itemId);
  if (item === undefined) {
    return null;
  }
  const arrivedSide = converterSideOfPort(arrivedOn);
  const capacity = portCapacity(item) ?? 0;
  for (let port = 1; port <= capacity; port += 1) {
    if (converterSideOfPort(port) === arrivedSide) {
      continue;
    }
    if (linkOn(connections, itemId, port, null, null, visited) !== undefined) {
      return port;
    }
  }
  return null;
}

/** The hops beyond (`itemId`, `port`) when leaving through `side` (any side for a device). */
function walk(
  index: ClientIndex,
  connections: readonly Connection[],
  itemId: string,
  port: number,
  side: PortSide | null,
  position: number | null,
  visited: Set<string>,
  visitedItems: Set<string>,
): TraceHop[] {
  const link = linkOn(connections, itemId, port, side, position, visited);
  if (link === undefined) {
    // A child hanging off this port (ROADMAP C10) continues the trace even
    // though it is no link of its own.
    const children = portChildHops(index, itemId, port, visitedItems);
    if (children.length > 0) {
      return children;
    }
    if (side !== 'rear') {
      return [];
    }
    // An unpatched run: the drop lands here and leads to its room.
    for (const drop of index.drops) {
      if (
        drop.fromNode?.type === 'item' &&
        drop.fromNode.id === itemId &&
        drop.fromNode.port === port
      ) {
        return [
          {
            itemId: '',
            itemName: drop.label,
            port: null,
            side: 'rear',
            dropLabel: drop.label,
            roomName: index.roomNames.get(drop.roomId) ?? null,
          },
        ];
      }
    }
    return [];
  }
  visited.add(link.id);
  const far = otherEnd(link, itemId);
  const farItem = index.items.get(far.itemId);
  const farPanel = isPanel(index, far.itemId);
  const arrivedOn = sideOf(link);
  const farPosition = far.position ?? null;
  const hop: TraceHop = {
    itemId: far.itemId,
    itemName: farItem?.name ?? 'Removed device',
    port: far.port,
    position: farPosition,
    side: farPanel ? arrivedOn : null,
    dropLabel:
      link.dropId === null
        ? null
        : (index.drops.find((drop) => drop.id === link.dropId)?.label ?? 'Removed drop'),
    roomName: null,
  };
  if (!farPanel || far.port === null) {
    // A converter or splitter carries the run on into its other side
    // (ROADMAP J5), which is how a fiber run traces through to the copper
    // device behind it.
    if (far.port !== null && isConverter(index, far.itemId)) {
      const exit = converterExit(index, connections, far.itemId, far.port, visited);
      if (exit !== null) {
        return [
          hop,
          ...walk(index, connections, far.itemId, exit, null, null, visited, visitedItems),
        ];
      }
    }
    return [hop];
  }
  // Through a breakout the run stays on its own leg: the rear position pairs
  // with the same position on the front (ROADMAP I2), so carry it across.
  const leaveBy: PortSide = arrivedOn === 'front' ? 'rear' : 'front';
  return [
    hop,
    ...walk(index, connections, far.itemId, far.port, leaveBy, farPosition, visited, visitedItems),
  ];
}

/**
 * The whole path through (`itemId`, `port`): for a device, where its link
 * leads, through every panel on the way; for a panel port, the far end of
 * the run behind it, the panel itself, and the far end of the patch in
 * front, in that order.
 */
export function tracePath(
  index: ClientIndex,
  connections: readonly Connection[],
  itemId: string,
  port: number,
  position: number | null = null,
): TraceHop[] {
  const item = index.items.get(itemId);
  if (item === undefined) {
    return [];
  }
  const here: TraceHop = {
    itemId,
    itemName: item.name,
    port,
    position,
    side: null,
    dropLabel: null,
    roomName: null,
  };
  const visited = new Set<string>();
  const visitedItems = new Set<string>([itemId]);
  // A converter has two sides like a panel, but they are different ports
  // rather than two faces of one (ROADMAP J5): the run behind it leaves by the
  // other side's port, so the trace is walked both ways from here as well.
  if (isConverter(index, itemId)) {
    const onward = walk(index, connections, itemId, port, null, position, visited, visitedItems);
    const exit = converterExit(index, connections, itemId, port, visited);
    const behind =
      exit === null
        ? []
        : walk(index, connections, itemId, exit, null, null, visited, visitedItems);
    return [...behind.toReversed(), here, ...onward];
  }
  if (!isPanel(index, itemId)) {
    return [here, ...walk(index, connections, itemId, port, null, position, visited, visitedItems)];
  }
  const rear = walk(index, connections, itemId, port, 'rear', position, visited, visitedItems);
  const front = walk(index, connections, itemId, port, 'front', position, visited, visitedItems);
  return [...rear.toReversed(), here, ...front];
}

/** One hop as a phrase: the device, its panel side, and its port. */
function hopText(hop: TraceHop): string {
  if (hop.itemId === '') {
    const room = hop.roomName === null ? '' : ` to ${hop.roomName}`;
    return `${hop.itemName}${room} (not patched)`;
  }
  const side = hop.side === null ? '' : ` ${PORT_SIDE_LABELS[hop.side]}`;
  const port = hop.port === null ? '' : ` port ${String(hop.port)}`;
  const leg =
    hop.position === null || hop.position === undefined ? '' : ` leg ${String(hop.position)}`;
  return `${hop.itemName}${side}${port}${leg}`;
}

/**
 * The whole physical path as one line, device to device through every panel
 * and run on the way: "Core port 3 → via D-01 → Panel B side port 12 →
 * Panel A side port 12 → Reception AP". The list trace (TracePath.tsx) draws
 * the same hops; this is the report's and the schedule's flat form of it.
 */
export function describePhysicalPath(hops: readonly TraceHop[]): string {
  if (hops.length <= 1) {
    return '';
  }
  const parts: string[] = [];
  for (const hop of hops) {
    if (hop.dropLabel !== null) {
      parts.push(`via ${hop.dropLabel}`);
    }
    parts.push(hopText(hop));
  }
  return parts.join(' → ');
}

/**
 * The state of one numbered port, in the four names the port faceplate uses
 * (shared/wiring-colors.ts): free, a run landed but not patched, patched on
 * one side of a panel, or patched through. The faceplate and this agree, so
 * the schedule, the map, and the port grid all colour a port the same.
 */
export function portTerminationState(
  index: ClientIndex,
  connections: readonly Connection[],
  itemId: string,
  port: number,
  position: number | null = null,
): PortStateName {
  // A breakout leg (ROADMAP I2) is a state of its own: a run and a landing
  // with no position sit on the first leg, so a leg reads only what is on it.
  const onPort = (end: Connection['a']): boolean =>
    end.itemId === itemId &&
    end.port === port &&
    (position === null || (end.position ?? null) === position);
  const landed =
    (position === null || position === 1) &&
    index.drops.some((drop) =>
      [drop.fromNode, drop.toNode].some(
        (node) => node?.type === 'item' && node.id === itemId && node.port === port,
      ),
    );
  const links = connections.filter((connection) =>
    [connection.a, connection.b].some((end) => onPort(end)),
  );
  if (links.length === 0) {
    return landed ? 'landed' : 'free';
  }
  if (!isPanel(index, itemId)) {
    return 'linked';
  }
  const frontFilled = links.some((connection) => sideOf(connection) === 'front');
  const rearFilled = landed || links.some((connection) => sideOf(connection) === 'rear');
  return frontFilled && rearFilled ? 'linked' : 'half';
}
